import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { ProgressBar } from "primereact/progressbar";
import { useContext, useMemo, useRef, useState } from "react";
import { useGetAllPayersQuery } from "store/queries/payers";
import {
  useDeleteGrouperSetMutation,
  useGenerateGrouperRateFileMutation,
  useGetGrouperSetsQuery,
  usePreviewGrouperSetMutation,
  useSaveGrouperSetMutation,
} from "store/queries/rateTools";
import styled from "styled-components";
import {
  IGrouperParseStats,
  IGrouperSet,
} from "types/PayerGrouper/PayerGrouper";

import { parseGrouperStats } from "./utils";

/**
 * CLE-2566 — Groupers sub-tab under Payer Rules → Rate File Tools.
 *
 * Aetna and UHC price big parts of their fee schedules by grouper category rather than per
 * CPT: their published workbook maps every CPT to a category ("2026 Grouper Category" 1-9
 * for Aetna, "OPG Group Number" 0-10 plus UL for UHC), and each contract supplies one flat
 * dollar rate per category. Analysts used to do that join by hand in Excel on every rate
 * change.
 *
 * Here the workbook is uploaded once and stored (a "grouper set"); generating a rate file
 * is then: pick the set, paste the category pricing, Generate. Categories the paste leaves
 * out are not dropped — their CPTs come out at $0.00 and are called out in the banner.
 *
 * Grouper sets are managed rather than hardcoded per payer so refreshing next year's Aetna
 * file (or adding a third payer) needs no deploy.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 900px;
`;

const Row = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;

  > div {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  label {
    font-size: 0.85em;
    color: var(--gray-700);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const ManageBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid var(--gray-300);
  border-radius: 6px;
  background: var(--gray-50);
`;

// Hoisted above the component to satisfy `@typescript-eslint/no-use-before-define`.
function extractErrorMessage(e: unknown): string {
  const err = e as {
    data?: { message?: string; error?: string } | string;
    message?: string;
  };
  const data = err?.data;
  if (typeof data === "string") return data;
  return (
    data?.message ?? data?.error ?? err?.message ?? "An unknown error occurred."
  );
}

/** 400 responses from the generate endpoint carry one message per bad pricing line. */
function extractLineErrors(e: unknown): string[] | null {
  const data = (e as { data?: { errors?: unknown } })?.data;
  if (data && Array.isArray(data.errors)) return data.errors.map(String);
  return null;
}

export function GrouperRateFile() {
  const { current: toastElement } = useContext(ToastContext);
  const { data: payersData } = useGetAllPayersQuery();
  const { data: setsData, isFetching: isLoadingSets } =
    useGetGrouperSetsQuery();

  const uploadRef = useRef<FileUpload>(null);

  // --- generate form ---
  const [grouperSetId, setGrouperSetId] = useState<string | null>(null);
  const [pricingText, setPricingText] = useState("");
  const [outputName, setOutputName] = useState("");
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [lineErrors, setLineErrors] = useState<string[] | null>(null);
  const [lastStats, setLastStats] = useState<ReturnType<
    typeof parseGrouperStats
  > | null>(null);

  // --- manage form ---
  const [showManage, setShowManage] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [sheetName, setSheetName] = useState<string | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [setName, setSetName] = useState("");
  const [payerId, setPayerId] = useState<number | null>(null);
  const [preview, setPreview] = useState<IGrouperParseStats | null>(null);
  const [manageError, setManageError] = useState<string | null>(null);

  const [generate, { isLoading: isGenerating }] =
    useGenerateGrouperRateFileMutation();
  const [previewSet, { isLoading: isPreviewing }] =
    usePreviewGrouperSetMutation();
  const [saveSet, { isLoading: isSaving }] = useSaveGrouperSetMutation();
  const [deleteSet] = useDeleteGrouperSetMutation();

  const sets: IGrouperSet[] = useMemo(() => setsData?.data ?? [], [setsData]);
  const selectedSet = sets.find((s) => s.id === grouperSetId) ?? null;

  const payerOptions = (payersData?.data ?? []).map((p: any) => ({
    label: p.name,
    value: p.id,
  }));

  const setOptions = sets.map((s) => ({
    label: `${s.name} (${s.entryCount.toLocaleString()} CPTs)`,
    value: s.id,
  }));

  const busyManaging = isPreviewing || isSaving;

  /**
   * Saving replaces by NAME, not by payer — two Aetna groupers (2026 and 2027) can coexist,
   * but re-using a name wipes that set's rows. Match the typed name so the operator knows
   * which of the two is about to happen before they hit Save.
   */
  const setBeingReplaced =
    sets.find(
      (s) => s.name.trim().toLowerCase() === setName.trim().toLowerCase()
    ) ?? null;

  const runGenerate = async () => {
    if (!grouperSetId || pricingText.trim() === "") return;
    setGenerateError(null);
    setLineErrors(null);
    setLastStats(null);

    try {
      const result = await generate({
        grouperSetId,
        pricingText,
        fileName: outputName.trim() || undefined,
      }).unwrap();

      setLastStats(parseGrouperStats(result.headers));

      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        outputName.trim() ||
        `${selectedSet?.name ?? "grouper"}-rates.csv`.replace(/\s+/g, "-");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toastElement?.show({
        severity: "success",
        summary: "Rate file generated",
        detail: "Your CSV is downloading.",
      });
    } catch (e: any) {
      const errors = extractLineErrors(e);
      if (errors) {
        setLineErrors(errors);
        setGenerateError("Category pricing has errors.");
        return;
      }
      setGenerateError(extractErrorMessage(e));
    }
  };

  const onSelectFile = async (selected: File | null) => {
    setFile(selected);
    setPreview(null);
    setManageError(null);
    setSheetNames([]);
    setSheetName(null);
    if (!selected) return;
    // Immediately parse so the operator sees the detected columns (and the sheet picker,
    // for the two-payers-in-one-workbook file) before committing anything.
    try {
      const result = await previewSet({ file: selected }).unwrap();
      setSheetNames(result.data.sheetNames);
      setPreview(result.data);
      if (!setName.trim()) {
        setSetName(selected.name.replace(/\.[^.]+$/, ""));
      }
    } catch (e: any) {
      // A multi-sheet workbook whose *first* sheet isn't a grouper still needs its sheet
      // list, so surface the picker even when the default parse failed.
      setManageError(extractErrorMessage(e));
    }
  };

  const onSelectSheet = async (nextSheet: string) => {
    setSheetName(nextSheet);
    setPreview(null);
    setManageError(null);
    if (!file) return;
    try {
      const result = await previewSet({ file, sheetName: nextSheet }).unwrap();
      setSheetNames(result.data.sheetNames);
      setPreview(result.data);
    } catch (e: any) {
      setManageError(extractErrorMessage(e));
    }
  };

  const runSave = async () => {
    if (!file || setName.trim() === "") return;
    setManageError(null);
    try {
      const result = await saveSet({
        file,
        sheetName: sheetName ?? undefined,
        name: setName.trim(),
        payerId,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: result.data.replaced ? "Grouper replaced" : "Grouper saved",
        detail: `${result.data.entryCount.toLocaleString()} CPTs stored for "${setName.trim()}".`,
      });
      uploadRef.current?.clear();
      setFile(null);
      setPreview(null);
      setSheetNames([]);
      setSheetName(null);
      setSetName("");
      setGrouperSetId(result.data.id);
    } catch (e: any) {
      setManageError(extractErrorMessage(e));
    }
  };

  /** Overwriting an existing grouper is destructive, so make it an explicit confirmation. */
  const confirmSave = () => {
    if (!setBeingReplaced) {
      runSave();
      return;
    }
    confirmDialog({
      header: "Replace grouper",
      message: `"${
        setBeingReplaced.name
      }" already exists with ${setBeingReplaced.entryCount.toLocaleString()} CPTs. Saving deletes those rows and stores this file in their place. Continue?`,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Replace",
      acceptClassName: "p-button-danger",
      accept: runSave,
    });
  };

  const onDelete = (entry: IGrouperSet) => {
    confirmDialog({
      header: "Delete grouper set",
      message: `Delete "${
        entry.name
      }"? This removes ${entry.entryCount.toLocaleString()} stored CPT rows. You can re-upload the workbook later.`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteSet(entry.id).unwrap();
          if (grouperSetId === entry.id) setGrouperSetId(null);
          toastElement?.show({
            severity: "success",
            summary: "Deleted",
            detail: "Grouper set removed.",
          });
        } catch (e: any) {
          toastElement?.show({
            severity: "error",
            summary: "Delete failed",
            detail: extractErrorMessage(e),
          });
        }
      },
    });
  };

  // Stable body renderers (react/no-unstable-nested-components).
  const payerCellBody = (entry: IGrouperSet) => entry.payerName ?? "—";
  const categoriesCellBody = (entry: IGrouperSet) =>
    entry.categories.join(", ") || "—";
  const updatedCellBody = (entry: IGrouperSet) =>
    new Date(entry.updatedAt).toLocaleString();
  const actionsCellBody = (entry: IGrouperSet) => (
    <Button
      icon="pi pi-trash"
      className="p-button-sm p-button-text p-button-danger"
      onClick={() => onDelete(entry)}
      aria-label="Delete"
    />
  );

  return (
    <Wrapper>
      <div>
        <h3 style={{ marginBottom: "0.25rem" }}>Grouper Rate File</h3>
        <p style={{ margin: 0, color: "var(--gray-600)" }}>
          Aetna and UHC price by grouper category, not per CPT. Pick the stored
          grouper, paste the negotiated <strong>flat dollar rate</strong> for
          each category, and generate the base rate file (
          <code>CPT Code, Description, Rate</code>). Categories you don&apos;t
          price come out at <code>0.00</code> and are listed after generating,
          so nothing is silently dropped.
        </p>
      </div>

      <Row>
        <div style={{ minWidth: 320 }}>
          <label htmlFor="grouper-set">Grouper</label>
          <Dropdown
            inputId="grouper-set"
            value={grouperSetId}
            options={setOptions}
            onChange={(e) => setGrouperSetId(e.value ?? null)}
            placeholder={
              sets.length === 0
                ? "No groupers stored yet — upload one below"
                : "Select grouper"
            }
            filter
            disabled={isGenerating || isLoadingSets}
          />
        </div>
        <div style={{ minWidth: 260 }}>
          <label htmlFor="grouper-output-name">
            Output file name (optional)
          </label>
          <InputText
            id="grouper-output-name"
            value={outputName}
            onChange={(e) => setOutputName(e.target.value)}
            placeholder="aetna-2026-rates.csv"
            disabled={isGenerating}
          />
        </div>
      </Row>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
        <label htmlFor="grouper-pricing" style={{ fontSize: "0.85em" }}>
          Category pricing — one <code>category rate</code> per line
        </label>
        <InputTextarea
          id="grouper-pricing"
          value={pricingText}
          onChange={(e) => setPricingText(e.target.value)}
          rows={8}
          autoResize
          placeholder={"1 1400\n2 1500\n3 1600"}
          disabled={isGenerating}
        />
        {selectedSet && (
          <small style={{ color: "var(--gray-700)" }}>
            Categories in <strong>{selectedSet.name}</strong>:{" "}
            <code>{selectedSet.categories.join(", ")}</code>
          </small>
        )}
        <small style={{ color: "var(--gray-600)" }}>
          Accepts <code>1 1400</code>, <code>1,1400</code>,{" "}
          <code>Category 1: $1,400.00</code>, tab-separated paste from Excel,
          and UHC&apos;s <code>UL</code> key.
        </small>
      </div>

      <ButtonRow>
        <Button
          label="Generate"
          icon="pi pi-arrow-right"
          onClick={runGenerate}
          disabled={!grouperSetId || pricingText.trim() === "" || isGenerating}
          loading={isGenerating}
        />
      </ButtonRow>

      {isGenerating && (
        <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
      )}

      {generateError && (
        <Message
          severity="error"
          content={
            <div>
              <div>{generateError}</div>
              {lineErrors && lineErrors.length > 0 && (
                <ul style={{ margin: "0.5rem 0 0 1.2rem", padding: 0 }}>
                  {lineErrors.map((err) => (
                    <li key={err}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
          }
          style={{ alignItems: "flex-start" }}
        />
      )}

      {lastStats && !generateError && (
        <Message
          severity={
            lastStats.unpricedCategories.length > 0 ||
            lastStats.unknownCategories.length > 0
              ? "warn"
              : "success"
          }
          content={
            <div>
              <div>
                <strong>
                  {lastStats.totalRows.toLocaleString()} rows generated
                </strong>{" "}
                ({lastStats.pricedRows.toLocaleString()} priced
                {lastStats.zeroRows > 0 &&
                  `, ${lastStats.zeroRows.toLocaleString()} at $0.00`}
                )
              </div>
              {lastStats.unpricedCategories.length > 0 && (
                <div style={{ marginTop: "0.25rem" }}>
                  No rate pasted for{" "}
                  {lastStats.unpricedCategories.length === 1
                    ? "category"
                    : "categories"}{" "}
                  <code>{lastStats.unpricedCategories.join(", ")}</code> — those
                  CPTs are in the file at <code>0.00</code>.
                </div>
              )}
              {lastStats.unknownCategories.length > 0 && (
                <div style={{ marginTop: "0.25rem" }}>
                  You priced{" "}
                  <code>{lastStats.unknownCategories.join(", ")}</code>, which{" "}
                  {lastStats.unknownCategories.length === 1 ? "is" : "are"} not
                  in this grouper — check for a typo.
                </div>
              )}
            </div>
          }
          style={{ alignItems: "flex-start" }}
        />
      )}

      <div>
        <Button
          label={showManage ? "Hide grouper management" : "Manage groupers"}
          icon={showManage ? "pi pi-chevron-up" : "pi pi-chevron-down"}
          className="p-button-text p-button-sm"
          onClick={() => setShowManage((v) => !v)}
        />
      </div>

      {showManage && (
        <>
          <ManageBox>
            <h4 style={{ margin: 0 }}>Upload a grouper workbook</h4>
            <p
              style={{ margin: 0, color: "var(--gray-600)", fontSize: "0.9em" }}
            >
              The sheet needs a CPT/HCPCS column and a grouper category column;
              a description column is used when present (Aetna has one, UHC
              doesn&apos;t). Groupers are keyed by <strong>name</strong>, not by
              payer: re-using a name replaces that grouper&apos;s rows, while a
              new name creates a second grouper (so 2026 and 2027 Aetna files
              can live side by side).
            </p>
            <Row>
              <div>
                <label>Grouper file (CSV or XLSX)</label>
                <FileUpload
                  ref={uploadRef}
                  mode="basic"
                  accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  chooseLabel={file ? file.name : "Choose file"}
                  customUpload
                  uploadHandler={() => undefined}
                  onSelect={(e) => onSelectFile(e.files[0] ?? null)}
                  disabled={busyManaging}
                />
              </div>
              {sheetNames.length > 1 && (
                <div style={{ minWidth: 200 }}>
                  <label htmlFor="grouper-sheet">Sheet</label>
                  <Dropdown
                    inputId="grouper-sheet"
                    value={sheetName ?? sheetNames[0]}
                    options={sheetNames.map((s) => ({ label: s, value: s }))}
                    onChange={(e) => onSelectSheet(e.value)}
                    disabled={busyManaging}
                  />
                </div>
              )}
              <div style={{ minWidth: 240 }}>
                <label htmlFor="grouper-name">Name</label>
                <InputText
                  id="grouper-name"
                  value={setName}
                  onChange={(e) => setSetName(e.target.value)}
                  placeholder="Aetna Enhanced Grouper 2026"
                  disabled={busyManaging}
                />
              </div>
              <div style={{ minWidth: 220 }}>
                <label htmlFor="grouper-payer">Payer (optional)</label>
                <Dropdown
                  inputId="grouper-payer"
                  value={payerId}
                  options={payerOptions}
                  onChange={(e) => setPayerId(e.value ?? null)}
                  placeholder="Select payer"
                  filter
                  showClear
                  disabled={busyManaging}
                />
              </div>
            </Row>

            <ButtonRow>
              <Button
                label={setBeingReplaced ? "Replace grouper" : "Save grouper"}
                icon="pi pi-upload"
                onClick={confirmSave}
                disabled={!file || setName.trim() === "" || busyManaging}
                loading={isSaving}
              />
            </ButtonRow>

            {setBeingReplaced && (
              <Message
                severity="warn"
                text={`"${
                  setBeingReplaced.name
                }" already exists (${setBeingReplaced.entryCount.toLocaleString()} CPTs, ${
                  setBeingReplaced.payerName ?? "no payer"
                }). Saving replaces all of its rows with this file.`}
                style={{ alignItems: "flex-start" }}
              />
            )}

            {busyManaging && (
              <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
            )}

            {manageError && (
              <Message
                severity="error"
                text={manageError}
                style={{ alignItems: "flex-start" }}
              />
            )}

            {preview && !manageError && (
              <Message
                severity="info"
                content={
                  <div>
                    <div>
                      Detected columns: <code>{preview.cptColumnHeader}</code> /{" "}
                      <code>{preview.categoryColumnHeader}</code>
                      {preview.descriptionColumnHeader ? (
                        <>
                          {" "}
                          / <code>{preview.descriptionColumnHeader}</code>
                        </>
                      ) : (
                        " (no description column — descriptions will be blank)"
                      )}
                    </div>
                    <div>
                      {preview.inputRows.toLocaleString()} rows read; categories{" "}
                      <code>{preview.categories.join(", ")}</code>
                    </div>
                    {(preview.droppedBlankCpt > 0 ||
                      preview.droppedBlankCategory > 0 ||
                      preview.droppedDuplicateCpt > 0) && (
                      <div
                        style={{ color: "var(--gray-700)", fontSize: "0.9em" }}
                      >
                        Skipped: {preview.droppedBlankCpt} blank CPT,{" "}
                        {preview.droppedBlankCategory} blank category,{" "}
                        {preview.droppedDuplicateCpt} duplicate CPT
                      </div>
                    )}
                  </div>
                }
                style={{ alignItems: "flex-start" }}
              />
            )}
          </ManageBox>

          <div>
            <h4 style={{ marginBottom: "0.5rem" }}>Stored groupers</h4>
            <DataTable
              value={sets}
              emptyMessage="No groupers stored yet."
              loading={isLoadingSets}
              size="small"
            >
              <Column field="name" header="Name" sortable />
              <Column header="Payer" body={payerCellBody} />
              <Column field="entryCount" header="CPTs" sortable />
              <Column header="Categories" body={categoriesCellBody} />
              <Column
                field="sourceFile"
                header="Source"
                body={(e: IGrouperSet) => e.sourceFile ?? "—"}
              />
              <Column header="Updated" body={updatedCellBody} />
              <Column header="Actions" body={actionsCellBody} />
            </DataTable>
          </div>

          <ConfirmDialog />
        </>
      )}
    </Wrapper>
  );
}

export default GrouperRateFile;
